import { useState } from "react";
import { Search, Star } from "lucide-react";

import { Input } from "@/ui/input";
import { cn } from "@/lib/utils";
import type { ReferralLink } from "@/features/affiliates/referral-link";
import type { CatalogProduct } from "@/features/products/catalogApi";
import { normalizeText } from "@/features/products/packageSearch";

/** Món chọn ở ô «Chọn sản phẩm»: sản phẩm đang đẩy, hoặc một gói trong bảng giá. */
export type ProductChoice =
  | { kind: "pushed"; key: string; productId: string; name: string }
  | {
      kind: "catalog";
      key: string;
      packageCode: string;
      name: string;
      concepts: { id: string; name: string }[];
    };

/**
 * Danh sách xổ xuống của ô «Chọn sản phẩm» (bản 2, CEO 1a 30/10/2026): ô tìm
 * không dấu, nhóm «Sản phẩm đang đẩy» ở đầu, rồi gói theo từng thương hiệu.
 * Nằm ngay trong hộp (không bật lớp nổi) để cuộn cùng thân hộp trên điện thoại.
 */
export function MyAffiliateProductList({
  id,
  pushed,
  products,
  conceptNames,
  brandLabel,
  loading,
  broken,
  selectedKey,
  onPick,
}: {
  id: string;
  pushed: ReferralLink[];
  products: CatalogProduct[];
  conceptNames: ReadonlyMap<string, string>;
  brandLabel: (code: string) => string;
  loading: boolean;
  broken: boolean;
  selectedKey: string | undefined;
  onPick: (choice: ProductChoice) => void;
}) {
  const [query, setQuery] = useState("");
  const needle = normalizeText(query);
  const hit = (name: string) => needle === "" || normalizeText(name).includes(needle);
  const pushedShown = pushed.filter((l) => hit(l.productName ?? ""));
  const groups: { brand: string; items: CatalogProduct[] }[] = [];
  for (const p of products) {
    if (!hit(p.name)) continue;
    const last = groups[groups.length - 1];
    if (last?.brand === p.brandCode) last.items.push(p);
    else groups.push({ brand: p.brandCode, items: [p] });
  }

  return (
    <div className="mt-1 overflow-hidden rounded-md border border-border bg-background shadow-md">
      <div className="flex items-center gap-2 border-b border-border px-3">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <Input
          type="search"
          aria-label="Tìm gói"
          placeholder="Tìm gói…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="border-0 px-0 shadow-none focus-visible:ring-0"
        />
      </div>
      <div id={id} role="listbox" aria-label="Sản phẩm" className="max-h-72 overflow-y-auto py-1">
        {pushedShown.length > 0 ? (
          <PushedGroup links={pushedShown} selectedKey={selectedKey} onPick={onPick} />
        ) : null}
        {groups.map((g) => (
          <BrandGroup
            key={g.brand}
            label={brandLabel(g.brand)}
            products={g.items}
            conceptNames={conceptNames}
            selectedKey={selectedKey}
            onPick={onPick}
          />
        ))}
        {loading ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">Đang tải bảng giá…</p>
        ) : broken ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">
            Chưa đọc được bảng giá — tạm chỉ chọn được sản phẩm đang đẩy.
          </p>
        ) : pushedShown.length === 0 && groups.length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">Không có gói nào khớp.</p>
        ) : null}
      </div>
    </div>
  );
}

function PushedGroup({
  links,
  selectedKey,
  onPick,
}: {
  links: ReferralLink[];
  selectedKey: string | undefined;
  onPick: (choice: ProductChoice) => void;
}) {
  return (
    <div role="group" aria-label="Sản phẩm đang đẩy">
      <GroupLabel>Sản phẩm đang đẩy</GroupLabel>
      {links.map((l) => {
        const key = `p:${l.productId ?? ""}`;
        const name = l.productName ?? "";
        return (
          <Option
            key={key}
            selected={selectedKey === key}
            onPick={() => onPick({ kind: "pushed", key, productId: l.productId ?? "", name })}
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <Star
                className="h-3.5 w-3.5 shrink-0 fill-current text-[var(--color-warning-text,#b27b00)]"
                aria-hidden
              />
              <span className="truncate">{name}</span>
            </span>
          </Option>
        );
      })}
    </div>
  );
}

function BrandGroup({
  label,
  products,
  conceptNames,
  selectedKey,
  onPick,
}: {
  label: string;
  products: CatalogProduct[];
  conceptNames: ReadonlyMap<string, string>;
  selectedKey: string | undefined;
  onPick: (choice: ProductChoice) => void;
}) {
  return (
    <div role="group" aria-label={label}>
      <GroupLabel>{label}</GroupLabel>
      {products.map((p) => {
        const key = `g:${p.code}`;
        const own = (p.concepts ?? [])
          .filter((cid) => conceptNames.has(cid))
          .map((cid) => ({ id: cid, name: conceptNames.get(cid) ?? cid }));
        return (
          <Option
            key={key}
            selected={selectedKey === key}
            onPick={() =>
              onPick({ kind: "catalog", key, packageCode: p.code, name: p.name, concepts: own })
            }
          >
            <span className="truncate">{p.name}</span>
            {own.length > 0 ? (
              <small className="shrink-0 text-xs text-muted-foreground">có concept</small>
            ) : null}
          </Option>
        );
      })}
    </div>
  );
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
      aria-hidden
    >
      {children}
    </div>
  );
}

function Option({
  selected,
  onPick,
  children,
}: {
  selected: boolean;
  onPick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onPick}
      className={cn(
        "flex min-h-11 w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-surface-2",
        selected && "bg-surface-2 font-medium",
      )}
    >
      {children}
    </button>
  );
}
